import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useDemoStore } from '@/stores/demoStore';
import { DEMO_EMAIL } from '@/demo/demoData';
import { apiClient } from '@/services/api';
import { AUTH_PORTAL_URL } from '@/config/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LUDOLIO_PRIVACY_POLICY_URL,
  LUDOLIO_TERMS_OF_USE_URL,
} from '@ludolio/shared-ui';

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const { clearError, setAuth } = useAuthStore();
  const enterDemo = useDemoStore((state) => state.enterDemo);
  const normalizedEmail = email.trim().toLowerCase();
  const isDemoEmail = normalizedEmail === DEMO_EMAIL;

  const [otpStep, setOtpStep] = useState<'email' | 'code'>('email');
  const [otpCode, setOtpCode] = useState('');
  const [otpFirstName, setOtpFirstName] = useState('');
  const [otpLastName, setOtpLastName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [nameError, setNameError] = useState('');

  const handleTryDemo = () => {
    const demoState = enterDemo();
    setAuth(demoState.currentUser);
    navigate({ to: '/dashboard' });
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDemoEmail) {
      handleTryDemo();
      return;
    }
    clearError();
    setOtpError('');

    try {
      setOtpLoading(true);
      const target = new URL('/dashboard', window.location.origin).href;
      const response = await apiClient.startInstitutionalLogin(email, target);
      if (!response.success) {
        throw new Error(response.error || (response as any).message || 'Failed to send code');
      }

      const data: any = response.data;
      if (data?.action === 'redirect_sso') {
        window.location.href = data.redirectUrl;
        return;
      }
      setIsNewUser(Boolean(data?.isNewUser));
      setOtpStep('code');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send code. Please try again.';
      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setOtpError('');
    setNameError('');

    if (isNewUser && (!otpFirstName.trim() || !otpLastName.trim())) {
      setNameError('Please enter your first and last name.');
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiClient.verifyOtp(
        email,
        otpCode,
        isNewUser ? otpFirstName.trim() : undefined,
        isNewUser ? otpLastName.trim() : undefined
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Invalid code');
      }

      const user = (response.data as any).user;
      if (!user) {
        throw new Error('User data is missing from response');
      }

      const needsOnboarding = Boolean(
        (response.data as any).needsOnboarding || (response.data as any).needsNameConfirmation
      );
      const needsOrgNoticeConsent = Boolean((response.data as any).needsOrgNoticeConsent);
      setAuth({ ...user, needsOnboarding, needsOrgNoticeConsent });
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Invalid code. Please try again.';
      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleInstitutionSignIn = () => {
    const url = new URL(AUTH_PORTAL_URL);
    url.searchParams.set('portal', 'teacher');
    url.searchParams.set('target', new URL('/dashboard', window.location.origin).href);
    window.location.href = url.href;
  };

  const handleBackToEmailStep = () => {
    setOtpStep('email');
    setOtpCode('');
    setOtpFirstName('');
    setOtpLastName('');
    setIsNewUser(false);
    setOtpError('');
    setNameError('');
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[hsl(var(--color-background))] px-4 py-8 text-[hsl(var(--color-foreground))]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_15%_15%,hsl(var(--color-primary)/0.14),transparent_30%),radial-gradient(circle_at_85%_10%,hsl(var(--color-primary)/0.08),transparent_28%),linear-gradient(180deg,hsl(var(--color-muted)/0.45),transparent_42%)]" />

      <Card className="relative w-full max-w-lg border-slate-200 bg-white/95 shadow-2xl shadow-slate-950/10 backdrop-blur">
        <CardHeader className="space-y-5 p-6 text-center sm:p-8">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Ludolio" className="h-12 w-auto" />
          </div>
          <div className="space-y-2">
            <CardTitle as="h1" className="text-2xl tracking-[-0.02em]">
              {otpStep === 'email' ? 'Instructor Dashboard' : 'Enter your login code'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {otpStep === 'email'
                ? ''
                : `We sent a 6-digit code to ${email}.`}
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-5 p-6 pt-0 sm:p-8 sm:pt-0">
          {otpStep === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2 text-left">
                <Label htmlFor="email">Your institutional email address</Label>
                <Input
                  id="email"
                  type="email"
                  className="h-11"
                  placeholder="instructor@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={otpLoading}
                  aria-invalid={!!otpError}
                  aria-describedby={otpError ? 'login-error' : undefined}
                />
              </div>
              {otpError && (
                <div id="login-error" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-950" role="alert">
                  {otpError}
                </div>
              )}
              {isDemoEmail ? (
                <>
                  <p className="text-xs text-center text-muted-foreground">
                    Demo mode — no verification required. Changes won't be saved.
                  </p>
                  <Button type="button" className="h-11 w-full" onClick={handleTryDemo}>
                    Try Demo
                  </Button>
                </>
              ) : (
                <Button
                  type="submit"
                  className="h-11 w-full"
                  disabled={otpLoading || !email}
                >
                  {otpLoading ? 'Checking sign-in…' : 'Continue'}
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full bg-white"
                disabled={otpLoading}
                onClick={handleInstitutionSignIn}
              >
                <Building2 className="mr-2 h-4 w-4" />
                Sign in with your institution
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4" noValidate>
              {isNewUser && (
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      className="h-11"
                      placeholder="First name"
                      value={otpFirstName}
                      onChange={(e) => {
                        setOtpFirstName(e.target.value);
                        if (e.target.value.trim() && otpLastName.trim()) {
                          setNameError('');
                        }
                      }}
                      required
                      disabled={otpLoading}
                      aria-invalid={!!nameError && !otpFirstName.trim()}
                      aria-describedby={nameError ? 'name-error' : undefined}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      className="h-11"
                      placeholder="Last name"
                      value={otpLastName}
                      onChange={(e) => {
                        setOtpLastName(e.target.value);
                        if (otpFirstName.trim() && e.target.value.trim()) {
                          setNameError('');
                        }
                      }}
                      required
                      disabled={otpLoading}
                      aria-invalid={!!nameError && !otpLastName.trim()}
                      aria-describedby={nameError ? 'name-error' : undefined}
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2 text-left">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  type="text"
                  className="h-11 tracking-[0.3em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={otpLoading}
                  autoFocus={!isNewUser}
                  aria-invalid={!!otpError}
                  aria-describedby={otpError ? 'login-error' : undefined}
                />
              </div>
              {nameError && (
                <div id="name-error" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-950" role="alert">
                  {nameError}
                </div>
              )}
              {otpError && (
                <div id="login-error" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-950" role="alert">
                  {otpError}
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <button
                  type="button"
                  onClick={handleBackToEmailStep}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  Change email
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => {
                    setOtpCode('');
                    setOtpError('');
                    handleSendOtp(new Event('submit') as any);
                  }}
                  disabled={otpLoading}
                >
                  Resend code
                </button>
              </div>
              <Button
                type="submit"
                className="h-11 w-full"
                disabled={otpLoading || otpCode.length < 6}
              >
                {otpLoading ? 'Verifying…' : 'Verify & continue'}
              </Button>
            </form>
          )}

          <p className="text-center text-xs text-muted-foreground">
            By logging in, you agree to our{' '}
            <a
              href={LUDOLIO_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[hsl(var(--color-primary-text))] underline transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href={LUDOLIO_TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[hsl(var(--color-primary-text))] underline transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
            >
              Terms of Use
            </a>.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
